import { t } from 'i18next';
import type {
    CustomerMatchReasonChildType,
    CustomerMatchReasonField,
    SearchMatchReason,
} from '../../../server/types/search-types.ts';

function orderAndFilterMatchReasons(
    matchReasons: SearchMatchReason[],
    orderArray: string[],
): SearchMatchReason[] {
    const highlights: SearchMatchReason[] = [];

    for (const reason of matchReasons) {
        const index = orderArray.indexOf(reason.field);

        if (index === -1) {
            continue;
        }

        highlights[index] ??= reason;
    }

    return highlights.filter(Boolean);
}

const customerHighlightOrder: string[] = [
    'ERP_ID',
    'NAME',
    'ADDRESS.CITY',
    'CONTACT.NAME',
    'CONTACT.PHONE_NUMBER',
    'CONTACT.MOBILE_NUMBER',
    'CONTACT.EMAIL',
    'ADDRESS.ZIP_CODE',
] as const satisfies CustomerMatchReasonField[];

export function orderAndFilterCustomerHighlights(matchReasons: SearchMatchReason[]): SearchMatchReason[] {
    return orderAndFilterMatchReasons(matchReasons, customerHighlightOrder);
}

function getMatchReasonPrefix(matchReason: SearchMatchReason): string | undefined {
    if (!matchReason.child_type) {
        return;
    }

    const childTypeToTranslationMapper: Record<string, string> = {
        address: t('search__match_label__address', { ns: 'common' }),
        contact: t('search__match_label__contact', { ns: 'common' }),
    } satisfies Record<CustomerMatchReasonChildType, string>;

    return childTypeToTranslationMapper[matchReason.child_type];
}

const primaryCustomerHighlightFields: string[] = [
    'ERP_ID',
    'NAME',
    'ADDRESS.CITY',
] satisfies CustomerMatchReasonField[];

export function selectCustomerHighlightsToDisplay(
    matchReasons: SearchMatchReason[],
): Partial<Record<CustomerMatchReasonField, { text: string; prefix: string | undefined }>> {
    const highlights = orderAndFilterCustomerHighlights(matchReasons);

    const isPrimaryField = (h: SearchMatchReason): boolean =>
        primaryCustomerHighlightFields.includes(h.field);

    // ID, ADDRESS, NAME when present, plus the next highest-priority highlight.
    const selected = [
        ...highlights.filter(isPrimaryField),
        ...highlights.filter((h) => !isPrimaryField(h)).slice(0, 1),
    ];

    return Object.fromEntries(
        selected.map((h) => [
            h.field,
            {
                text: h.text,
                prefix: getMatchReasonPrefix(h),
            },
        ]),
    );
}
